import { useCallback, useMemo, useState, type ReactNode } from 'react'

import {
  LocationChangeContext,
  type CartLineAtBranch,
} from '../hooks/useLocationChange'

const STORAGE_KEY = 'engineerCartLinesAtLocationChange'

function readLineBranches(): Record<string, string> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, string>
    }
  } catch {
    // Corrupt or blocked storage — showing no warnings is the safe fallback.
  }
  return {}
}

function writeLineBranches(lineBranches: Record<string, string>): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lineBranches))
  } catch {
    // Storage full or blocked — the warnings just won't survive a reload.
  }
}

/**
 * Remembers which branch each basket line was at when the engineer last
 * changed location (DEV-51046), so the cart can flag collection branches that
 * are now far away.
 *
 * Kept in localStorage so the warning survives a reload or a new tab. It lasts
 * until the line moves to another branch or leaves the basket (removed,
 * cleared or ordered): the stored branch then no longer matches a line in the
 * cart. A line added after the move was never recorded, so a basket that
 * started far away shows the plain distance (AC4).
 */
export function LocationChangeProvider({ children }: { children: ReactNode }) {
  const [lineBranchesAtMove, setLineBranchesAtMove] = useState(readLineBranches)

  const markLocationChanged = useCallback((lines: CartLineAtBranch[]) => {
    // Replace, not merge: the basket as it is now is the whole picture, so
    // lines that left it since the last move drop out here.
    const next: Record<string, string> = {}
    for (const line of lines) {
      if (line.id && line.branchId != null) next[line.id] = String(line.branchId)
    }
    writeLineBranches(next)
    setLineBranchesAtMove(next)
  }, [])

  const value = useMemo(
    () => ({ lineBranchesAtMove, markLocationChanged }),
    [lineBranchesAtMove, markLocationChanged],
  )

  return (
    <LocationChangeContext.Provider value={value}>
      {children}
    </LocationChangeContext.Provider>
  )
}
